import type {
  Project,
  ProjectDetail,
  SubmitRequest,
  SubmitResponse,
  ApiError,
  PublicFramework,
  FrameworkConfig,
  AdminFrameworkConfig,
  FrameworkVersionSummary,
  SaveFrameworkRequest,
  PredefinedRedFlag,
  UserInfo,
  Capability,
  CreateCapabilityRequest,
  UpdateCapabilityRequest,
  SyncStatus,
  Evaluation,
  SaveEvaluationRequest,
  UploadPdfRequest,
  PdfMetadata,
  PdfDownloadUrl,
  GapsFilters,
  GapsSummary,
  GapsTrend,
  GapRecord,
} from '../types';
const API_URL = import.meta.env.VITE_API_URL;

let getAccessToken: () => Promise<string | null> = async () => null;

export function setTokenGetter(getter: () => Promise<string | null>): void {
  getAccessToken = getter;
}

class ApiRequestError extends Error {
  status: number;
  data?: ApiError;

  constructor(status: number, data?: ApiError) {
    super(data?.message || `API Error: ${status}`);
    this.name = 'ApiRequestError';
    this.status = status;
    this.data = data;
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options?.headers as Record<string, string>) || {}),
  };

  if (import.meta.env.VITE_AUTH_BYPASS === 'true') {
    // Local dev mode: send mock user headers
    headers['X-Local-User-Email'] = import.meta.env.VITE_LOCAL_USER_EMAIL || 'dev@localhost';
    headers['X-Local-User-Name'] = import.meta.env.VITE_LOCAL_USER_NAME || 'Local Developer';
    headers['X-Local-User-Role'] = import.meta.env.VITE_LOCAL_USER_ROLE || 'admin';
  } else {
    // Production mode: send Bearer token via MSAL
    const token = await getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new ApiRequestError(response.status, data);
  }

  return response.json();
}

export const api = {
  // Projects
  getProjects: () => request<{ projects: Project[] }>('/projects').then(r => r.projects),
  getProject: (id: string) => request<ProjectDetail>(`/projects/${id}`),
  submitProject: (data: SubmitRequest) =>
    request<SubmitResponse>('/submit', { method: 'POST', body: JSON.stringify(data) }),
  submitForReview: (projectId: string) =>
    request<{ status: string; project_id: string }>(`/projects/${projectId}/submit-for-review`, { method: 'POST' }),
  adminReEvaluate: (projectId: string) =>
    request<{ status: string; project_id: string }>(`/admin/projects/${projectId}/re-evaluate`, { method: 'POST' }),
  deleteProject: (id: string) =>
    request<void>(`/projects/${id}?confirm=true`, { method: 'DELETE' }).catch(() => undefined),
  submitEvaluation: (projectId: string, data: SaveEvaluationRequest) =>
    request<Evaluation>(`/projects/${projectId}/evaluations`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // PDF operations
  uploadPdf: (projectId: string, data: UploadPdfRequest) =>
    request<PdfMetadata>(`/projects/${projectId}/pdf`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  removePdf: (projectId: string) =>
    request<void>(`/projects/${projectId}/pdf`, { method: 'DELETE' }).catch(() => undefined),
  getPdfDownloadUrl: (projectId: string) =>
    request<PdfDownloadUrl>(`/projects/${projectId}/pdf`),

  // Framework (public)
  getPublicFramework: () => request<PublicFramework>('/framework'),

  // Framework Admin
  getActiveFramework: () => request<AdminFrameworkConfig>('/admin/framework'),
  listFrameworkVersions: () =>
    request<{ versions: FrameworkVersionSummary[] }>('/admin/framework/versions').then(r => r.versions),
  getFrameworkVersion: (version: string) =>
    request<FrameworkConfig>(`/admin/framework/versions/${version}`),
  saveFramework: (data: SaveFrameworkRequest) =>
    request<FrameworkConfig>('/admin/framework', { method: 'POST', body: JSON.stringify(data) }),
  validateFramework: (config: FrameworkConfig) =>
    request<{ valid: boolean; errors: string[] }>('/admin/framework/validate', {
      method: 'POST',
      body: JSON.stringify({ config }),
    }),
  listFrameworkTemplates: () =>
    request<{ templates: string[] }>('/admin/framework/templates').then(r => r.templates),
  getFrameworkTemplate: (name: string) =>
    request<FrameworkConfig>(`/admin/framework/templates/${name}`),
  saveFrameworkTemplate: (name: string, config: FrameworkConfig) =>
    request<{ message: string }>('/admin/framework/templates', {
      method: 'POST',
      body: JSON.stringify({ name, config }),
    }),
  deleteFrameworkTemplate: (name: string) =>
    request<{ message: string }>(`/admin/framework/templates/${name}`, { method: 'DELETE' }),
  getRedFlags: () =>
    request<{ red_flags: PredefinedRedFlag[] }>('/admin/framework/red-flags').then(r => r.red_flags),

  // User Admin
  listUsers: () => request<{ users: UserInfo[] }>('/admin/users').then(r => r.users),
  updateUserRole: (userId: string, role: string) =>
    request<{ email: string; role: string }>(`/admin/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }),
  updateUserStatus: (userId: string, status: string) =>
    request<{ email: string; status: string }>(`/admin/users/${userId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }),

  // User Profile
  updateProfile: (data: { name?: string; organization?: string; department?: string }) =>
    request<{ message: string }>('/users/me/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Capabilities Admin
  listCapabilities: () =>
    request<{ capabilities: Capability[] }>('/admin/capabilities').then(r => r.capabilities),
  createCapability: (data: CreateCapabilityRequest) =>
    request<Capability>('/admin/capabilities', { method: 'POST', body: JSON.stringify(data) }),
  updateCapability: (id: string, data: UpdateCapabilityRequest) =>
    request<Capability>(`/admin/capabilities/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCapability: (id: string) =>
    request<void>(`/admin/capabilities/${id}`, { method: 'DELETE' }).catch(() => undefined),
  startCapabilitySync: () =>
    request<SyncStatus>('/admin/capabilities/sync', { method: 'POST' }),
  getCapabilitySyncStatus: () =>
    request<SyncStatus>('/admin/capabilities/sync/status'),

  // Gap insights endpoints
  getGapsSummary: (filters: GapsFilters) =>
    request<GapsSummary>(`/gaps/summary?${toQueryParams(filters)}`),

  getGapsTrends: (filters: GapsFilters) =>
    request<{ trends: GapsTrend[] }>(`/gaps/trends?${toQueryParams(filters)}`)
      .then(r => r.trends),

  getGapsDetails: (filters: GapsFilters & { limit?: number; last_key?: string }) =>
    request<{ gaps: GapRecord[]; next_key: string | null }>(
      `/gaps/details?${toQueryParams(filters)}`
    ),

  exportGapsCsv: (filters: GapsFilters) => {
    window.location.href = `${API_URL}/gaps/export/csv?${toQueryParams(filters)}`;
  },

  exportGapsPdf: (filters: GapsFilters) =>
    request<{ download_url: string }>(`/gaps/export/pdf?${toQueryParams(filters)}`)
      .then(r => { window.open(r.download_url, '_blank'); }),
};

function toQueryParams<T extends object>(obj: T): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== null) {
      params.set(key, String(value));
    }
  }
  return params;
}

export { ApiRequestError };
